import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  QrCode, TrendingUp, TrendingDown, Package, Tag, FileText,
  MessageSquare, RefreshCw, ArrowUpRight, CheckCircle, Clock,
  XCircle, MapPin, Building2, AlertTriangle, Zap, Target,
  ChevronRight, Activity, Wallet,
} from 'lucide-react';
import { format, subDays, startOfMonth, differenceInDays, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { gql } from '@apollo/client';

import {
  GET_LABORATORY_BY_USER,
  GET_PROMOTIONS_BY_LABORATORY,
  GET_PRODUCTS_BY_LABORATORY,
  GET_CONTRACTS_BY_LABORATORY,
} from '../../api/laboratory-api';
import { GET_TICKETS_BY_USER } from '../../api/messages-api';
import { authApi } from '../../api/auth-api';

// ─── GraphQL ──────────────────────────────────────────────────────────────────
// Scoped query: fetches only the transactions of this laboratory (filtered at DB level)
const GET_LAB_TRANSACTIONS = gql`
  query GetLabTransactions($laboratoryId: ID!) {
    transactionsByLaboratory(laboratoryId: $laboratoryId) {
      id
      date
      gain
      companyCommission
      status
      product { id name price laboratoryId }
      pharmacy {
        id namePharmacy
        region { id nomRegion }
        ville { id nomVilleVillage }
      }
      label { id code promotion { id name } }
      createdAt
    }
  }
`;

// ─── Palette ──────────────────────────────────────────────────────────────────
const GREEN   = '#16a34a';
const GREEN_L = '#dcfce7';
const CHART_COLORS = ['#16a34a', '#2563eb', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const STATUS_CFG = {
  IN_PROGRESS: { label: 'Disponible', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
  PENDING:     { label: 'En attente', color: '#d97706', bg: '#fef9c3', icon: Clock },
  VALIDATED:   { label: 'Versé',      color: '#2563eb', bg: '#dbeafe', icon: CheckCircle },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD', maximumFractionDigits: 0 }).format(n);

const pct = (n) => `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;

// ─── Sub-components ───────────────────────────────────────────────────────────
const KpiCard = ({ icon: Icon, iconBg, iconColor, label, value, sub, trend, trendVal, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex flex-col gap-3
      ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
  >
    <div className="flex items-center justify-between">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg}`}>
        <Icon size={18} className={iconColor} />
      </div>
      {trend !== undefined && (
        <span className={`flex items-center gap-0.5 text-xs font-semibold ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          {trend >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {Math.abs(trendVal ?? trend).toFixed(1)}%
        </span>
      )}
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
    {sub && <p className="text-xs text-gray-400 -mt-1">{sub}</p>}
  </div>
);

const SectionTitle = ({ children, action, actionLabel }) => (
  <div className="flex items-center justify-between mb-3">
    <h2 className="text-sm font-semibold text-gray-700">{children}</h2>
    {action && (
      <button onClick={action} className="text-xs text-green-700 font-medium flex items-center gap-1 hover:underline">
        {actionLabel} <ChevronRight size={12} />
      </button>
    )}
  </div>
);

// Custom tooltip for recharts
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: <span className="font-bold">{p.value}</span></p>
      ))}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const MainDashboard = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('30d');
  const [productFilter, setProductFilter]     = useState('all');
  const [promotionFilter, setPromotionFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  const userData = authApi.getUserData();
  const userId = userData?.id;

  const { data: labData, loading: labLoading, error: labError } = useQuery(GET_LABORATORY_BY_USER, {
    variables: { userId }, skip: !userId, fetchPolicy: 'cache-and-network',
  });
  const lab = labData?.laboratoryByUserId;
  const labId = lab?.id ?? null;

  // Transactions scoped to this laboratory — only runs once labId is known
  const { data: txData, loading: txLoading, refetch: refetchTx } = useQuery(GET_LAB_TRANSACTIONS, {
    variables: { laboratoryId: labId ?? '' },
    skip: !labId,
    fetchPolicy: 'cache-and-network',
    // No pollInterval — refetch manually via the refresh button to avoid hammering the DB
  });
  const { data: promoData, loading: promoLoading, refetch: refetchPromo } = useQuery(GET_PROMOTIONS_BY_LABORATORY, {
    variables: { laboratoryId: labId ?? '' }, skip: !labId, fetchPolicy: 'cache-and-network',
  });
  const { data: productsData, loading: productsLoading, refetch: refetchProducts } = useQuery(GET_PRODUCTS_BY_LABORATORY, {
    variables: { laboratoryId: labId ?? '' }, skip: !labId, fetchPolicy: 'cache-and-network',
  });
  const { data: contractsData, refetch: refetchContracts } = useQuery(GET_CONTRACTS_BY_LABORATORY, {
    variables: { laboratoryId: labId ?? '' }, skip: !labId, fetchPolicy: 'cache-and-network',
  });
  const { data: msgData, refetch: refetchMsg } = useQuery(GET_TICKETS_BY_USER, {
    variables: { userId: userId ?? '' }, skip: !userId, fetchPolicy: 'cache-and-network',
  });

  const isLoading = txLoading || promoLoading || productsLoading;

  // Transactions are already filtered server-side — no client-side lab filter needed
  const allTx    = txData?.transactionsByLaboratory ?? [];
  const promotions = promoData?.promotionsByLaboratory ?? [];
  const products   = productsData?.productsByLaboratory ?? [];
  const contracts  = contractsData?.contractsByLaboratory ?? [];
  const messages   = msgData?.ticketsByUser ?? [];

  // ── Base filter (product + promotion, no date) ───────────────────────────
  const baseTx = useMemo(() => {
    let result = allTx;
    if (productFilter !== 'all')   result = result.filter(t => t.product?.id === productFilter);
    if (promotionFilter !== 'all') result = result.filter(t => t.label?.promotion?.id === promotionFilter);
    return result;
  }, [allTx, productFilter, promotionFilter]);

  // ── Period filter ─────────────────────────────────────────────────────────
  const periodStart = useMemo(() => {
    if (dateFrom) return new Date(dateFrom);
    const now = new Date();
    if (period === '1d')  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (period === '7d')  return subDays(now, 7);
    if (period === '30d') return subDays(now, 30);
    return subDays(now, 90); // quarter
  }, [period, dateFrom]);

  const periodEnd = useMemo(() => {
    if (dateTo) return new Date(dateTo + 'T23:59:59');
    return new Date();
  }, [dateTo]);

  const prevPeriodStart = useMemo(() => {
    const len = differenceInDays(periodEnd, periodStart);
    return subDays(periodStart, Math.max(len, 1));
  }, [periodStart, periodEnd]);

  const periodTx = useMemo(() => baseTx.filter(t => {
    const d = new Date(t.date);
    return d >= periodStart && d <= periodEnd;
  }), [baseTx, periodStart, periodEnd]);

  const prevTx = useMemo(() => baseTx.filter(t => {
    const d = new Date(t.date);
    return d >= prevPeriodStart && d < periodStart;
  }), [baseTx, prevPeriodStart, periodStart]);

  // ── Core KPIs ─────────────────────────────────────────────────────────────
  const kpi = useMemo(() => {
    const totalScans    = periodTx.length;
    const prevScans     = prevTx.length;
    const scanChange    = prevScans > 0 ? ((totalScans - prevScans) / prevScans) * 100 : 0;

    const totalGain     = periodTx.reduce((s, t) => s + (t.gain ?? 0), 0);
    const prevGain      = prevTx.reduce((s, t) => s + (t.gain ?? 0), 0);
    const gainChange    = prevGain > 0 ? ((totalGain - prevGain) / prevGain) * 100 : 0;

    const totalCommission = periodTx.reduce((s, t) => s + (t.gain ?? 0) * ((t.companyCommission ?? 0) / 100), 0);

    // QR scan rate (scanned / generated)
    const totalGenerated = promotions.reduce((s, p) => s + (p.quantity ?? 0), 0);
    const scanRate       = totalGenerated > 0 ? (baseTx.length / totalGenerated) * 100 : 0;

    // Active promos
    const now = new Date();
    const activePromos = promotions.filter(p => p.status === 'IN_PROGRESS' && new Date(p.endDate) >= now).length;
    const draftPromos  = promotions.filter(p => p.status === 'DRAFT').length;
    const closedPromos = promotions.filter(p => p.status === 'CLOSED').length;

    // Contracts
    const activeContracts  = contracts.filter(c => new Date(c.dateEnd) >= now).length;
    const expiringContracts = contracts.filter(c => {
      const d = new Date(c.dateEnd);
      return d >= now && differenceInDays(d, now) <= 30;
    }).length;

    // Unread messages
    const unreadMsgs = messages.filter(m => m.hasUnreadMessages).length;

    // Avg gain per scan
    const avgGain = totalScans > 0 ? totalGain / totalScans : 0;

    return {
      totalScans, scanChange,
      totalGain, gainChange,
      totalCommission,
      scanRate, totalGenerated,
      activePromos, draftPromos, closedPromos,
      activeContracts, expiringContracts,
      unreadMsgs,
      avgGain,
    };
  }, [periodTx, prevTx, promotions, contracts, messages, baseTx.length]);

  // ── Daily scan trend (last N days) ────────────────────────────────────────
  const dailyTrend = useMemo(() => {
    const days = period === '1d' ? 1 : period === '7d' ? 7 : period === '30d' ? 30 : 90;
    const out = [];
    for (let i = days - 1; i >= 0; i--) {
      const d   = subDays(new Date(), i);
      const ds  = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const de  = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
      const day = baseTx.filter(t => { const td = new Date(t.date); return td >= ds && td < de; });
      out.push({
        date:  format(d, 'dd/MM', { locale: fr }),
        scans: day.length,
        gain:  Math.round(day.reduce((s, t) => s + (t.gain ?? 0), 0)),
      });
    }
    return out;
  }, [baseTx, period]);

  // ── Status breakdown ─────────────────────────────────────────────────────
  const statusBreakdown = useMemo(() => {
    const map = {};
    periodTx.forEach(t => {
      const cfg = STATUS_CFG[t.status] ?? { label: t.status, color: '#94a3b8' };
      if (!map[t.status]) map[t.status] = { name: cfg.label, value: 0, color: cfg.color };
      map[t.status].value++;
    });
    return Object.values(map);
  }, [periodTx]);

  // ── Product ranking ───────────────────────────────────────────────────────
  const productRanking = useMemo(() => {
    const map = {};
    periodTx.forEach(t => {
      if (!t.product) return;
      if (!map[t.product.id]) map[t.product.id] = { name: t.product.name, scans: 0, gain: 0 };
      map[t.product.id].scans++;
      map[t.product.id].gain += t.gain ?? 0;
    });
    return Object.values(map).sort((a, b) => b.scans - a.scans).slice(0, 6);
  }, [periodTx]);

  // ── Geographic distribution ───────────────────────────────────────────────
  const geoDistrib = useMemo(() => {
    const map = {};
    periodTx.forEach(t => {
      const region = t.pharmacy?.region?.nomRegion ?? 'Inconnue';
      map[region] = (map[region] ?? 0) + 1;
    });
    const total = periodTx.length || 1;
    return Object.entries(map)
      .map(([region, count]) => ({ region, count, pct: Math.round((count / total) * 100) }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 7);
  }, [periodTx]);

  // ── Promo performance ─────────────────────────────────────────────────────
  const promoPerf = useMemo(() => {
    const map = {};
    periodTx.forEach(t => {
      const name = t.label?.promotion?.name ?? 'Sans promotion';
      if (!map[name]) map[name] = { name, scans: 0, gain: 0 };
      map[name].scans++;
      map[name].gain += t.gain ?? 0;
    });
    return Object.values(map).sort((a, b) => b.scans - a.scans).slice(0, 4);
  }, [periodTx]);

  // ── Refresh ───────────────────────────────────────────────────────────────
  const handleRefresh = () => {
    Promise.all([refetchTx(), refetchPromo(), refetchProducts(), refetchContracts(), refetchMsg()])
      .then(() => toast.success('Données actualisées'))
      .catch(() => toast.error('Erreur lors de l\'actualisation'));
  };

  // ── Loading / Error ───────────────────────────────────────────────────────
  if (labLoading && !lab) return (
    <div className="flex items-center justify-center h-64 gap-3">
      <div className="h-6 w-6 animate-spin rounded-full border-4 border-green-600 border-r-transparent" />
      <span className="text-sm text-gray-500">Chargement...</span>
    </div>
  );
  if (labError || !lab) return (
    <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-center gap-3">
      <AlertTriangle className="text-red-500" />
      <p className="text-sm text-red-700">Laboratoire introuvable. Contactez l'administrateur.</p>
    </div>
  );

  const maxDailyScans = Math.max(...dailyTrend.map(d => d.scans), 1);

  return (
    <div className="space-y-6 pb-8">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-[#1a3a1a] to-[#2d5a2d] rounded-2xl p-6 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center font-bold text-lg">
              {(lab.nom ?? '?').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold leading-tight">{lab.nom}</h1>
              <p className="text-green-200 text-sm">Tableau de bord analytique</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={period}
              onChange={e => { setPeriod(e.target.value); setDateFrom(''); setDateTo(''); }}
              className="bg-white/15 text-white border border-white/25 rounded-xl px-3 py-2 text-sm focus:outline-none"
            >
              <option value="1d"      className="text-gray-900">Ce jour</option>
              <option value="7d"      className="text-gray-900">7 jours</option>
              <option value="30d"     className="text-gray-900">30 jours</option>
              <option value="quarter" className="text-gray-900">3 mois</option>
            </select>
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="w-9 h-9 bg-white/15 hover:bg-white/25 rounded-xl flex items-center justify-center transition"
            >
              <RefreshCw size={15} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Quick stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          {[
            { label: 'Scans (période)', value: kpi.totalScans.toLocaleString('fr-FR') },
            { label: 'QR générés (total)', value: kpi.totalGenerated.toLocaleString('fr-FR') },
            { label: 'Promos actives', value: kpi.activePromos },
            { label: 'Contrats actifs', value: kpi.activeContracts },
          ].map(s => (
            <div key={s.label} className="bg-white/10 rounded-xl px-4 py-3">
              <p className="text-green-100 text-xs mb-0.5">{s.label}</p>
              <p className="text-white font-bold text-lg">{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Filters row ──────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Product filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Produit</label>
            <select
              value={productFilter}
              onChange={e => { setProductFilter(e.target.value); setPromotionFilter('all'); }}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white"
            >
              <option value="all">Tous les produits</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {/* Promotion filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Promotion</label>
            <select
              value={promotionFilter}
              onChange={e => setPromotionFilter(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white"
            >
              <option value="all">Toutes les promotions</option>
              {promotions
                .filter(p => productFilter === 'all' || p.products?.some(pr => pr.id === productFilter))
                .map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {/* Date from */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date début</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white"
            />
          </div>

          {/* Date to */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date fin</label>
            <div className="flex gap-2">
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white"
              />
              {(productFilter !== 'all' || promotionFilter !== 'all' || dateFrom || dateTo) && (
                <button
                  onClick={() => { setProductFilter('all'); setPromotionFilter('all'); setDateFrom(''); setDateTo(''); }}
                  className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors flex-shrink-0"
                >
                  Réinitialiser
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Solde dotation commissions ───────────────────────────────────── */}
      {(() => {
        const soldeRestant       = lab?.soldeRestant ?? 0;
        const commissionsAcquises = allTx.reduce((s, t) => s + (t.gain ?? 0), 0);
        const dotationRecue      = soldeRestant + commissionsAcquises;
        const usagePct           = dotationRecue > 0 ? Math.min(100, (commissionsAcquises / dotationRecue) * 100) : 0;
        const barColor           = usagePct >= 90 ? '#ef4444' : usagePct >= 70 ? '#f59e0b' : '#16a34a';
        const soldeColor         = dotationRecue === 0 ? 'text-gray-400' : soldeRestant < 0 ? 'text-red-600' : soldeRestant < dotationRecue * 0.2 ? 'text-amber-600' : 'text-green-700';
        return (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center">
                  <Wallet size={16} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700">Dotation commissions</p>
                  <p className="text-xs text-gray-400">Budget alloué par Mazid</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400 mb-0.5">Solde restant</p>
                <span className={`text-2xl font-bold ${soldeColor}`}>{fmt(soldeRestant)}</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center mb-3">
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-0.5">Dotation reçue</p>
                <p className="text-base font-bold text-gray-800">{fmt(dotationRecue)}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-0.5">Commissions acquises</p>
                <p className="text-base font-bold text-red-600">{fmt(commissionsAcquises)}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-0.5">Solde restant</p>
                <p className={`text-base font-bold ${soldeColor}`}>{fmt(soldeRestant)}</p>
              </div>
            </div>
            {dotationRecue > 0 && (
              <div>
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>Consommé</span><span>{usagePct.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div className="h-2 rounded-full transition-all" style={{ width: `${usagePct}%`, backgroundColor: barColor }} />
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── KPI Cards ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        <KpiCard
          icon={QrCode}
          iconBg="bg-green-100" iconColor="text-green-600"
          label="Scans QR"
          value={kpi.totalScans.toLocaleString('fr-FR')}
          trend={kpi.scanChange} trendVal={kpi.scanChange}
          sub="vs période précédente"
        />
        <KpiCard
          icon={Zap}
          iconBg="bg-emerald-100" iconColor="text-emerald-600"
          label="Gains vendeurs"
          value={fmt(kpi.totalGain)}
          trend={kpi.gainChange} trendVal={kpi.gainChange}
          sub="commissions versées"
        />
        <KpiCard
          icon={Target}
          iconBg="bg-blue-100" iconColor="text-blue-600"
          label="Taux de scan"
          value={`${kpi.scanRate.toFixed(1)}%`}
          sub={`${baseTx.length} / ${kpi.totalGenerated} QR`}
        />
        <KpiCard
          icon={Activity}
          iconBg="bg-violet-100" iconColor="text-violet-600"
          label="Gain moyen / scan"
          value={fmt(kpi.avgGain)}
          sub="par scan vendeur"
        />
        <KpiCard
          icon={TrendingUp}
          iconBg="bg-orange-100" iconColor="text-orange-600"
          label="Commission Mazid"
          value={fmt(kpi.totalCommission)}
          sub="5% du CA scanné"
        />
      </div>

      {/* ── Charts row ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Scan + gain trend (2/3) */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <SectionTitle>Évolution des scans et des gains</SectionTitle>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={dailyTrend} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
              <defs>
                <linearGradient id="gScans" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#16a34a" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gGain" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#2563eb" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                interval={dailyTrend.length > 20 ? Math.floor(dailyTrend.length / 10) : 'preserveStartEnd'} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="scans" name="Scans"  stroke="#16a34a" strokeWidth={2} fill="url(#gScans)" dot={false} />
              <Area type="monotone" dataKey="gain"  name="Gain (DH)" stroke="#2563eb" strokeWidth={2} fill="url(#gGain)"  dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Status breakdown (1/3) */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <SectionTitle>Statut des transactions</SectionTitle>
          {statusBreakdown.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={statusBreakdown} cx="50%" cy="50%" innerRadius={48} outerRadius={72}
                    dataKey="value" paddingAngle={3}>
                    {statusBreakdown.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {statusBreakdown.map(s => (
                  <div key={s.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
                      {s.name}
                    </span>
                    <span className="font-semibold">{s.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-[200px] text-gray-400">
              <Activity size={28} className="mb-2 text-gray-300" />
              <p className="text-sm">Aucune transaction</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Product ranking + Geographic distribution ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Product ranking */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <SectionTitle action={() => navigate('/laboratory/products')} actionLabel="Voir produits">
            Performance des produits
          </SectionTitle>
          {productRanking.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={productRanking}
                layout="vertical"
                margin={{ top: 0, right: 40, bottom: 0, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={90}
                  tick={{ fontSize: 10, fill: '#374151' }} axisLine={false} tickLine={false}
                  tickFormatter={v => v.length > 14 ? v.slice(0, 13) + '…' : v} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="scans" name="Scans" fill="#16a34a" radius={[0, 4, 4, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[200px] text-gray-400">
              <Package size={28} className="mb-2 text-gray-300" />
              <p className="text-sm">Aucun scan sur la période</p>
            </div>
          )}
        </div>

        {/* Geographic distribution */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <SectionTitle>Répartition géographique</SectionTitle>
          {geoDistrib.length > 0 ? (
            <div className="space-y-3">
              {geoDistrib.map((r, i) => (
                <div key={r.region}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="flex items-center gap-1.5 text-xs text-gray-600 font-medium">
                      <MapPin size={11} className="text-green-500 flex-shrink-0" />
                      {r.region}
                    </span>
                    <span className="text-xs font-bold text-gray-800">{r.count} <span className="font-normal text-gray-400">scans</span></span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${r.pct}%`,
                        background: `hsl(${130 - i * 15}, 60%, 45%)`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[200px] text-gray-400">
              <MapPin size={28} className="mb-2 text-gray-300" />
              <p className="text-sm">Aucune donnée géographique</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Promo perf + Recent tx + QR funnel ───────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Promotion performance */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <SectionTitle action={() => navigate('/laboratory/promotions')} actionLabel="Voir tout">
            Performances promos
          </SectionTitle>
          {promoPerf.length > 0 ? (
            <div className="space-y-3">
              {promoPerf.map((p, i) => (
                <div key={p.name} className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full bg-green-100 text-green-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{p.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-green-600 font-semibold">{p.scans} scans</span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-blue-600">{fmt(p.gain)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[160px] text-gray-400">
              <Tag size={24} className="mb-2 text-gray-300" />
              <p className="text-sm">Aucune promo active</p>
            </div>
          )}

          {/* Promo status pills */}
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-2 text-center">
            {[
              { label: 'Actives',   count: kpi.activePromos,   color: 'text-green-600', bg: 'bg-green-50' },
              { label: 'En attente', count: kpi.draftPromos,   color: 'text-amber-600',  bg: 'bg-amber-50' },
              { label: 'Terminées', count: kpi.closedPromos,   color: 'text-gray-500',  bg: 'bg-gray-50'  },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-xl py-2`}>
                <p className={`text-lg font-bold ${s.color}`}>{s.count}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent transactions */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <SectionTitle action={() => navigate('/laboratory/transactions')} actionLabel="Voir tout">
            Transactions récentes
          </SectionTitle>
          {allTx.length > 0 ? (
            <div className="space-y-2">
              {allTx.slice(0, 6).map(t => {
                const cfg = STATUS_CFG[t.status] ?? { label: t.status, color: '#94a3b8', bg: '#f1f5f9' };
                return (
                  <div key={t.id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: cfg.bg }}>
                      <QrCode size={14} style={{ color: cfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">
                        {t.product?.name ?? '—'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {t.pharmacy?.namePharmacy ?? '—'} · {format(new Date(t.date), 'dd/MM HH:mm', { locale: fr })}
                      </p>
                    </div>
                    <span className="text-xs font-bold text-green-600 flex-shrink-0">+{fmt(t.gain)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[200px] text-gray-400">
              <Activity size={28} className="mb-2 text-gray-300" />
              <p className="text-sm">Aucune transaction</p>
            </div>
          )}
        </div>

        {/* QR Funnel + Contracts + Messages */}
        <div className="space-y-4">

          {/* QR Code Funnel */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Entonnoir QR codes</h3>
            {[
              { label: 'QR générés',  count: kpi.totalGenerated, color: '#2563eb', pct: 100 },
              { label: 'QR scannés',  count: baseTx.length,       color: '#16a34a', pct: kpi.totalGenerated > 0 ? (baseTx.length / kpi.totalGenerated) * 100 : 0 },
              { label: 'Gains validés', count: baseTx.filter(t => t.status === 'VALIDATED').length, color: '#7c3aed',
                pct: baseTx.length > 0 ? (baseTx.filter(t => t.status === 'VALIDATED').length / baseTx.length) * 100 : 0 },
            ].map((f, i) => (
              <div key={f.label} className="mb-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600 font-medium">{f.label}</span>
                  <span className="font-bold" style={{ color: f.color }}>{f.count.toLocaleString('fr-FR')}</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${f.pct}%`, background: f.color }} />
                </div>
                {i < 2 && (
                  <p className="text-xs text-gray-400 text-right mt-0.5">{f.pct.toFixed(1)}%</p>
                )}
              </div>
            ))}
          </div>

          {/* Contracts + Messages */}
          <div className="grid grid-cols-2 gap-3">
            <div
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate('/laboratory/contracts')}
            >
              <div className="flex items-center justify-between mb-2">
                <FileText size={15} className="text-purple-500" />
                <ArrowUpRight size={12} className="text-gray-400" />
              </div>
              <p className="text-xl font-bold text-gray-900">{kpi.activeContracts}</p>
              <p className="text-xs text-gray-500">Contrats actifs</p>
              {kpi.expiringContracts > 0 && (
                <p className="text-xs text-amber-500 mt-1 flex items-center gap-1">
                  <AlertTriangle size={10} /> {kpi.expiringContracts} expirent bientôt
                </p>
              )}
            </div>

            <div
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate('/laboratory/messages')}
            >
              <div className="flex items-center justify-between mb-2">
                <MessageSquare size={15} className="text-red-500" />
                {kpi.unreadMsgs > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 font-bold">
                    {kpi.unreadMsgs}
                  </span>
                )}
              </div>
              <p className="text-xl font-bold text-gray-900">{messages.length}</p>
              <p className="text-xs text-gray-500">Messages</p>
              {kpi.unreadMsgs > 0 && (
                <p className="text-xs text-red-500 mt-1">{kpi.unreadMsgs} non lu(s)</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Alert banner ─────────────────────────────────────────────────── */}
      {(kpi.unreadMsgs > 0 || kpi.expiringContracts > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-4">
          <AlertTriangle size={18} className="text-amber-500 flex-shrink-0" />
          <div className="flex-1 text-sm text-amber-800">
            {kpi.unreadMsgs > 0 && <span><strong>{kpi.unreadMsgs}</strong> message(s) non lu(s). </span>}
            {kpi.expiringContracts > 0 && <span><strong>{kpi.expiringContracts}</strong> contrat(s) expirent dans 30 jours.</span>}
          </div>
          <div className="flex gap-2">
            {kpi.unreadMsgs > 0 && (
              <button onClick={() => navigate('/laboratory/messages')}
                className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded-lg font-medium">
                Messages
              </button>
            )}
            {kpi.expiringContracts > 0 && (
              <button onClick={() => navigate('/laboratory/contracts')}
                className="text-xs border border-amber-400 text-amber-700 px-3 py-1.5 rounded-lg font-medium">
                Contrats
              </button>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default MainDashboard;
